import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, Container, Paper, CircularProgress, Divider, Grid } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { getAssinaturaByHash } from '../../services/assinaturaService';

const ValidacaoAssinaturaPage: React.FC = () => {
    const { hash } = useParams<{ hash: string }>();
    const [loading, setLoading] = useState(true);
    const [assinaturaData, setAssinaturaData] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchAssinatura = async () => {
            if (!hash) return;
            try {
                setLoading(true);
                const data = await getAssinaturaByHash(hash);
                if (!data) {
                    setError('Documento não encontrado ou hash inválido.');
                } else {
                    setAssinaturaData(data);
                }
            } catch (err: any) {
                setError('Assinatura não encontrada ou hash inválido.');
            } finally {
                setLoading(false);
            }
        };
        fetchAssinatura();
    }, [hash]);

    if (loading) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8 }}>
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <CancelOutlinedIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
                    <Typography variant="h5" color="error" gutterBottom>Assinatura Inválida</Typography>
                    <Typography variant="body1">{error}</Typography>
                </Paper>
            </Container>
        );
    }

    const dados = assinaturaData?.dados_assinatura || {};
    
    // Calcula o valor total do recibo
    let valorTotalLiquido = 0;
    if (assinaturaData && assinaturaData.lancamentos_financeiros) {
        const lf = assinaturaData.lancamentos_financeiros;
        const items = lf.lancamento_itens || [];
        let itemsSum = 0;
        for (const item of items) {
            itemsSum += (item.quantidade * item.valor_unitario) - (item.desconto_valor || 0);
        }
        valorTotalLiquido = itemsSum - (lf.desconto_valor || 0);
    }

    return (
        <Container maxWidth="sm" sx={{ mt: 8 }}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
                <CheckCircleOutlineIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
                <Typography variant="h5" color="success.main" gutterBottom>
                    Documento Autêntico e Válido
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 4 }}>
                    Este documento foi assinado digitalmente e sua autenticidade foi confirmada pelo sistema.
                </Typography>
                
                <Divider sx={{ my: 2 }} />
                
                <Box sx={{ textAlign: 'left', mt: 3 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant="caption" color="textSecondary">Assinado por:</Typography>
                            <Typography variant="body2">{dados.nome_digitado || assinaturaData?.nome_assinante}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="caption" color="textSecondary">Autenticação via:</Typography>
                            <Typography variant="body2">{assinaturaData?.email_assinante}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="caption" color="textSecondary">Valor Total do Documento:</Typography>
                            <Typography variant="body2">
                                {valorTotalLiquido > 0 
                                    ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valorTotalLiquido)
                                    : '-'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="textSecondary">Data/Hora da Assinatura:</Typography>
                            <Typography variant="body2">
                                {dados.data_assinatura ? new Date(dados.data_assinatura).toLocaleString('pt-BR') : '-'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="textSecondary">Endereço de IP:</Typography>
                            <Typography variant="body2">{dados.ip_address || '-'}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="caption" color="textSecondary">Hash de Validação:</Typography>
                            <Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                {dados.hash_assinatura || hash}
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>
            </Paper>
        </Container>
    );
};

export default ValidacaoAssinaturaPage;
